import React, { useState, useEffect, useRef } from 'react';
import { Button, Space, message, Popconfirm, Alert } from 'antd';
import {
    AudioOutlined,
    PlayCircleOutlined,
    PauseCircleOutlined,
    DeleteOutlined,
    StopOutlined
} from '@ant-design/icons';
import { useAudioRecorder } from '../../hooks/useAudioRecorder';

interface AudioRecorderProps {
    language: string;  // 'greek' or 'hebrew'
    word: string | null;  // The actual Greek/Hebrew word
    onRecordingComplete?: () => void;
}

export const AudioRecorder: React.FC<AudioRecorderProps> = ({
    language,
    word,
    onRecordingComplete
}) => {
    const [hasExistingAudio, setHasExistingAudio] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const {
        isRecording,
        isUploading,
        error,
        startRecording,
        stopRecording,
        uploadAudio,
        deleteAudio,
        checkAudioExists,
        getAudioUrl,
        clearError,
    } = useAudioRecorder();

    // Check if audio exists when word or language changes
    useEffect(() => {
        if (word && language) {
            setIsChecking(true);
            checkAudioExists(language, word)
                .then(setHasExistingAudio)
                .finally(() => setIsChecking(false));
        } else {
            setHasExistingAudio(false);
        }
    }, [language, word, checkAudioExists]);

    // Cleanup audio on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const handleRecordClick = async () => {
        if (isRecording) {
            const blob = await stopRecording();
            if (blob && word && language) {
                const success = await uploadAudio(language, word, blob);
                if (success) {
                    message.success('Audio saved successfully');
                    setHasExistingAudio(true);
                    onRecordingComplete?.();
                } else {
                    message.error('Failed to save audio');
                }
            }
        } else {
            clearError();
            await startRecording();
        }
    };

    const handlePlayClick = () => {
        if (!word || !language) return;

        if (isPlaying && audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
        } else {
            // Create new audio element with cache-busting parameter
            const audio = new Audio(getAudioUrl(language, word) + `?t=${Date.now()}`);
            audio.onended = () => setIsPlaying(false);
            audio.onerror = () => {
                message.error('Failed to play audio');
                setIsPlaying(false);
            };
            audio.play().catch(() => {
                message.error('Failed to play audio');
                setIsPlaying(false);
            });
            audioRef.current = audio;
            setIsPlaying(true);
        }
    };

    const handleDeleteClick = async () => {
        if (!word || !language) return;

        // Stop playback if playing
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
            setIsPlaying(false);
        }

        const success = await deleteAudio(language, word);
        if (success) {
            message.success('Audio deleted');
            setHasExistingAudio(false);
        } else {
            message.error('Failed to delete audio');
        }
    };

    if (!word) {
        return <span style={{ color: '#999', fontStyle: 'italic' }}>Save the word first to add audio</span>;
    }

    return (
        <div>
            {error && (
                <Alert
                    message={error}
                    type="error"
                    showIcon
                    closable
                    onClose={clearError}
                    style={{ marginBottom: 12 }}
                />
            )}
            <Space wrap>
                <Button
                    type={isRecording ? 'primary' : 'default'}
                    danger={isRecording}
                    icon={isRecording ? <StopOutlined /> : <AudioOutlined />}
                    onClick={handleRecordClick}
                    loading={isUploading}
                    disabled={isChecking}
                >
                    {isRecording ? 'Stop & Save' : hasExistingAudio ? 'Re-record' : 'Record'}
                </Button>

                {hasExistingAudio && (
                    <>
                        <Button
                            icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                            onClick={handlePlayClick}
                            disabled={isRecording}
                        >
                            {isPlaying ? 'Stop' : 'Play'}
                        </Button>
                        <Popconfirm
                            title="Delete audio recording? This cannot be undone."
                            onConfirm={handleDeleteClick}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                disabled={isRecording}
                            >
                                Delete
                            </Button>
                        </Popconfirm>
                    </>
                )}

                {isRecording && (
                    <span style={{ color: '#ff4d4f', fontWeight: 500 }}>
                        Recording...
                    </span>
                )}
            </Space>
        </div>
    );
};
